import { BillingDetailsForm } from '@/components/billing/BillingDetailsForm'
import { BillingSummary } from '@/components/billing/BillingSummary.tsx'
import { RequireAccess } from '@/components/require-access'
import {
	absoluteMinBudgetEuros,
	calculateCampaignDurationInDays,
	calculateMinBudgetByDuration,
	centsToEuros,
	defaultCampaignMaxBudget,
	maxCampaignDurationDays,
	overlapsPeakAdSeason,
	recommendedDailyBudgetCents,
} from '@/constants'
import { useIsJobillan, useJobillaOrgId } from '@/hooks'
import { useQuotaRequestMore } from '@/hooks'
import { formatPeriodEnd, useAuth, useEntitlements } from '@/lib'
import { AmountField, Button, DateRangeField, FormDialog, logger, TextField, useAlert } from '@/ui'
import {
	campaignDetailsLocationAtom,
	campaignDetailsRequirementsAtom,
	campaignDetailsTitleAtom,
	campaignReviewAtom,
} from '@/ui'
import {
	CampaignAddonData,
	campaignChannel,
	CustomFieldData,
	CustomFieldWithValueData,
	locationEnum,
	type SubmitCampaignForReviewData,
	useBudgetPredictionGetPrediction,
	useCampaignAddonShow,
	useCampaignInvoiceStatus,
	useCampaignsShow,
	useCustomerValidateBillingDetails,
	useCustomerWalletCardTopup,
	useCustomerWalletShow,
	useCustomFieldList,
	useInvoiceSettingsShow,
	useOrganisationRelationShow,
	useProfilesIndex,
} from '@jobilla/nexus-sdk'
import { useDebouncedCallback } from '@jobilla/starfield'
import { useIsMutating } from '@tanstack/react-query'
import { useAtom, useAtomValue } from 'jotai'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { AdCampaignCity, AdCampaignLocationSelector } from './ad-campaign-location-selector'
import { BudgetPrediction } from './budget-prediction.tsx'
import { CampaignAddon } from './campaign-addon.tsx'
import { ChannelSelector } from './channel-selector'
import { CompletePopup, CompletePopupRef } from './complete-popup'

export type ReviewFormProps = {
	campaignId: string
	serviceFeePercent: number
	onReviewComplete: (
		paymentMethods: SubmitCampaignForReviewData['paymentMethods'],
		stripePaymentMethodId?: string,
		profileId?: string | null,
		inventoryItems?: SubmitCampaignForReviewData['inventoryItems'],
	) => Promise<void>
	onRedirectAfterComplete: () => void
	renderPreviews: () => Promise<{ feedAdBlob: Blob; storyAdBlob: Blob }>
}

export function ReviewForm(
	{ campaignId, serviceFeePercent, onReviewComplete, onRedirectAfterComplete, renderPreviews }: ReviewFormProps,
) {
	const { t, i18n } = useTranslation('editor')
	const { campaignQuota, canSendToReview, isFullyServiced } = useEntitlements()
	const campaignTitle = useAtomValue(campaignDetailsTitleAtom)
	const campaignLocation = useAtomValue(campaignDetailsLocationAtom)
	const campaignRequirements = useAtomValue(campaignDetailsRequirementsAtom)
	const [review, setReview] = useAtom(campaignReviewAtom)
	const [customFields, setCustomFields] = useState<Record<string, string>>({})
	const completePopupRef = useRef<CompletePopupRef>(null)
	const budgetOnFocusRef = useRef<number | null>(null)
	const alert = useAlert()
	const organisationId = useJobillaOrgId()

	const orgQuery = useOrganisationRelationShow(organisationId, { enabled: !!organisationId })
	const careerSiteEnabled = orgQuery.data?.careerSiteEnabled ?? false
	const hasMetaChannel = !careerSiteEnabled || (review?.channels.includes(campaignChannel.META) ?? true)

	const requestMore = useQuotaRequestMore(campaignQuota.periodEnd)
	const serviceTierUpgradeRequest = useQuotaRequestMore(null, 'upgrade-request:service-tier')

	const walletTopupMutation = useCustomerWalletCardTopup({
		mutationKey: ['customer-hosted-checkout-review'],
		onSuccess: (data) => {
			window.open(data.url, '_blank', 'noopener,noreferrer')
		},
		onError: () => {
			logger.error('Editor wallet top-up session creation failed', {
				data: { organisation_id: organisationId, campaign_id: campaignId },
			})
			alert.open(
				t('review.submissionFailed'),
				t('review.submissionFailedMessage'),
				t('review.ok'),
				() => {},
			)
		},
	})

	const wallet = useCustomerWalletShow(organisationId, {
		queryKey: ['customer-wallet-show-review'],
		enabled: !!organisationId,
	})

	const walletCredits = wallet.data?.balance ?? 0

	const { can } = useAuth()
	const [isEditingBillingDetails, setIsEditingBillingDetails] = useState(false)

	const profiles = useProfilesIndex(organisationId, { enabled: !!organisationId && can('profile:edit') })
	const primaryProfile = profiles.data?.data.find((profile) => profile.isPrimary) ?? null

	const budgetPrediction = useBudgetPredictionGetPrediction()

	// Initialize fields from review state once when component mounts
	useEffect(() => {
		if (review?.custom_fields && Object.keys(customFields).length === 0) {
			setCustomFields(review.custom_fields)
		}
	}, []) // Only run once on mount

	const campaignAddons = useCampaignAddonShow(campaignId)

	const invoiceCustomFields = useCustomFieldList(organisationId, locationEnum.invoice)
	const campaignShow = useCampaignsShow(campaignId, { staleTime: 1000 })
	const isMissingImage = !!campaignShow.data && [
		campaignShow.data.feed?.image,
		campaignShow.data.story?.image,
		campaignShow.data.welcomeScreen?.image,
	].some((image) => !image || image.isPlaceholder)

	// Include any custom fields already attached to this campaign — even if they have
	// since been soft-deleted at the organisation level — so users can keep editing
	// or removing pre-existing values.
	const renderableCustomFields: CustomFieldData[] = useMemo(() => {
		const orgFields = invoiceCustomFields.data?.fields ?? []
		const attachedFields: CustomFieldWithValueData[] = campaignShow.data?.customFields ?? []
		const orgIds = new Set(orgFields.map((field) => field.id))

		const orphanedAttached: CustomFieldData[] = attachedFields
			.filter((field) => !orgIds.has(field.id))
			.map((field) => ({
				id: field.id,
				name: field.name,
				type: 'text',
				location: 'invoice',
				isRequired: false,
			}))

		return [...orgFields, ...orphanedAttached]
	}, [invoiceCustomFields.data?.fields, campaignShow.data?.customFields])

	// Debounce campaign details for prediction API
	const debouncedBudgetPredictionMutation = useDebouncedCallback(
		{ wait: 500, trailing: true },
		budgetPrediction.mutate,
		[],
	)

	const invoiceStatus = useCampaignInvoiceStatus(organisationId, campaignId, {
		enabled: !!organisationId,
	})

	const hasExistingInvoice = !!invoiceStatus.data?.invoice

	const billingValidation = useCustomerValidateBillingDetails(
		organisationId,
		undefined,
		{
			enabled: !!organisationId,
			staleTime: 5 * 60 * 1000, // 5 minutes - avoid refetch during Stripe search index delay
		},
	)

	const hasUnverifiedTaxID = billingValidation.data?.customer?.tax?.preventPurchase

	const invoiceSettings = useInvoiceSettingsShow(organisationId, {
		enabled: !!organisationId,
		staleTime: 5 * 60 * 1000,
	})

	const invoicingEnabled = invoiceSettings.data?.canPayViaInvoice ?? false
	const externalInvoicing = invoiceSettings.data?.externalInvoicing ?? false
	const isDemo = invoiceSettings.data?.isDemo ?? false

	// Trigger budget prediction when campaign details change (debounced)
	useEffect(() => {
		const hasLocations = review?.adCampaignLocations && review.adCampaignLocations.length > 0

		if (!hasLocations) {
			budgetPrediction.reset()
			return
		}

		if (campaignTitle && campaignRequirements && !hasExistingInvoice) {
			debouncedBudgetPredictionMutation({
				data: {
					jobTitle: campaignTitle,
					jobDescription: campaignRequirements,
					locations: review.adCampaignLocations,
					campaignId: campaignId,
				},
			})
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps -- budgetPrediction.reset is stable
	}, [
		debouncedBudgetPredictionMutation,
		campaignTitle,
		campaignRequirements,
		hasExistingInvoice,
		review?.adCampaignLocations,
		campaignId,
	])

	// Store predicted budget in review state when prediction is received (convert cents to euros)
	useEffect(() => {
		const predictedBudgetCents = budgetPrediction.data?.recommendedBudget
		const predictedBudgetEuros = predictedBudgetCents ? Math.round(centsToEuros(predictedBudgetCents)) : null
		setReview((prev) => {
			if (!prev) { return prev }
			if (prev.predictedBudget === predictedBudgetEuros) { return prev }
			return { ...prev, predictedBudget: predictedBudgetEuros }
		})
	}, [budgetPrediction.data?.recommendedBudget, setReview])

	// Get today's date at midnight for comparison
	const today = useMemo(() => {
		const date = new Date()
		date.setHours(0, 0, 0, 0)
		return date
	}, [])

	// Calculate duration from date range for budget validation
	const duration = useMemo(() => {
		if (!review) {
			return 0
		}
		return calculateCampaignDurationInDays(review.startDate, review.endDate)
	}, [review?.startDate, review?.endDate])

	// Handle date range change
	const handleDateRangeChange = (dateRange: [Date, Date]) => {
		if (!review) {
			return
		}
		const [startDate, endDate] = dateRange
		setReview({
			...review,
			startDate,
			endDate,
		})
	}

	const minBudget = useMemo(() => {
		return centsToEuros(calculateMinBudgetByDuration(duration, serviceFeePercent))
	}, [duration, serviceFeePercent])

	const maxBudget = centsToEuros(defaultCampaignMaxBudget)

	const budgetErrors = useMemo(() => {
		if (!review) {
			return []
		}
		if (review.budget < minBudget) {
			if (minBudget > absoluteMinBudgetEuros) {
				return [t('review.budgetMinDuration', { duration, min: minBudget })]
			}
			return [t('review.budgetMinAbsolute', { min: absoluteMinBudgetEuros })]
		}
		if (review.budget > maxBudget) {
			return [t('review.budgetMax', { max: maxBudget.toLocaleString() })]
		}
		return []
	}, [review?.budget, minBudget, maxBudget, duration, t])

	const budgetHint = useMemo(() => {
		const hasPrediction = !!budgetPrediction.data?.recommendedBudget

		if (hasPrediction) {
			return undefined
		}

		const recommendedDailyEuros = centsToEuros(recommendedDailyBudgetCents)
		return t('review.budgetHint', { amount: recommendedDailyEuros })
	}, [budgetPrediction.data, t])

	const isMissingRequiredCustomField = useMemo(() => {
		if (!hasMetaChannel) { return false }
		const requiredFields = invoiceCustomFields.data?.fields?.filter((field) => field.isRequired) ?? []
		return requiredFields.some((field) => !customFields[field.id] || customFields[field.id].trim() === '')
	}, [hasMetaChannel, invoiceCustomFields.data?.fields, customFields])

	const isBillingComplete = billingValidation.data?.validation.valid ?? false

	const inflightReviewSaves = useIsMutating({ mutationKey: ['update-campaign-review'] })
	const inflightAddonStores = useIsMutating({ mutationKey: ['campaign-addon-store', campaignId] })
	const inflightAddonRemoves = useIsMutating({ mutationKey: ['campaign-addon-remove', campaignId] })
	const isPersistingChanges = inflightReviewSaves + inflightAddonStores + inflightAddonRemoves > 0

	const isJobillan = useIsJobillan()
	const hasCreditedInvoice = invoiceStatus.data?.invoice?.credited ?? false
	const isCreditedInvoiceBlocking = hasCreditedInvoice && !isJobillan

	const hasCustomFieldValueTooLong = useMemo(() => {
		if (!hasMetaChannel) { return false }
		return Object.values(customFields).some((value) => value.length > 140)
	}, [hasMetaChannel, customFields])

	const isStartDateInPast = useMemo(() => {
		if (!review?.startDate) { return false }
		const startDate = new Date(review.startDate)
		startDate.setHours(0, 0, 0, 0)
		const now = new Date()
		now.setHours(0, 0, 0, 0)
		return startDate < now
	}, [review?.startDate])

	const dateErrors = useMemo(() => {
		if (isStartDateInPast) {
			return [t('review.startDateInPast')]
		}
		return []
	}, [isStartDateInPast, t])

	const handleAdCampaignLocationsChange = useCallback(
		(cities: AdCampaignCity[]) => {
			setReview((prev) => {
				if (!prev) { return prev }
				return { ...prev, adCampaignLocations: cities }
			})
		},
		[setReview],
	)

	const setCustomFieldValue = useCallback((fieldId: string, value: string) => {
		setCustomFields((prev) => ({
			...prev,
			[fieldId]: value,
		}))
	}, [])

	// Sync custom fields to review state
	useEffect(() => {
		if (review) {
			setReview((prev) => {
				if (!prev) { return prev }
				if (prev.custom_fields === customFields) { return prev }
				return { ...prev, custom_fields: customFields }
			})
		}
	}, [customFields, setReview, review])

	const trackBudgetSet = useCallback((
		previousBudget: number | undefined,
		newBudget: number,
		budgetSource: 'manual' | 'predicted',
	) => {
		const predictedBudget = review?.predictedBudget ?? null

		logger.track('Editor: Changed budget', {
			data: {
				campaign_id: campaignId,
				organisation_id: organisationId,
				budget: newBudget,
				previous_budget: previousBudget,
				predicted_budget: predictedBudget,
				budget_source: budgetSource,
				company: organisationId,
			},
		})
	}, [campaignId, organisationId, review?.predictedBudget])

	const handleBudgetFocus = useCallback(() => {
		budgetOnFocusRef.current = review?.budget ?? null
	}, [review?.budget])

	const handleBudgetBlur = useCallback(() => {
		const previousBudget = budgetOnFocusRef.current
		const currentBudget = review?.budget

		if (previousBudget != null && currentBudget != null && currentBudget !== previousBudget) {
			trackBudgetSet(previousBudget, currentBudget, 'manual')
		}

		budgetOnFocusRef.current = null
	}, [review?.budget, trackBudgetSet])

	const handleComplete = () => {
		if (!canSendToReview && isFullyServiced) {
			serviceTierUpgradeRequest.request()
			alert.open(
				t('review.serviceTierDialogTitle'),
				t('review.serviceTierDialogMessage'),
				t('review.ok'),
				() => {},
			)
			return
		}
		completePopupRef.current?.open()
	}

	const handleConfirm = async (
		paymentMethods: SubmitCampaignForReviewData['paymentMethods'],
		stripePaymentMethodId?: string,
		profileId?: string | null,
		inventoryItems?: SubmitCampaignForReviewData['inventoryItems'],
	) => {
		// Errors are surfaced per-step in the completion progress view, so we let
		// them propagate to the popup rather than raising a separate alert.
		await onReviewComplete(paymentMethods, stripePaymentMethodId, profileId, inventoryItems)
	}

	if (!review) {
		return null
	}

	return (
		<div className="flex flex-col gap-4 mt-2">
			<div className="grid grid-cols-[2fr_1fr] gap-4 p-4 bg-grey-50 rounded-lg">
				<div className="flex flex-col gap-1">
					<div className="text-sm text-grey-500">{t('review.campaignTitle')}</div>
					<div className="text-lg font-semibold text-grey-900">{campaignTitle}</div>
				</div>
				<div className="flex flex-col gap-1">
					<div className="text-sm text-grey-500">{t('review.jobLocation', 'Job Location')}</div>
					<div className="text-lg font-semibold text-grey-900">{campaignLocation}</div>
				</div>
			</div>

			{careerSiteEnabled && (
				<ChannelSelector
					channels={review.channels}
					onChange={(channels) => setReview({ ...review, channels })}
					careerSiteEnabled={careerSiteEnabled}
				/>
			)}

			{hasMetaChannel && (
				<>
					<div className="p-4 bg-grey-50 rounded-lg">
						<AdCampaignLocationSelector
							cities={review.adCampaignLocations}
							onChange={handleAdCampaignLocationsChange}
							readOnly={hasExistingInvoice}
						/>
					</div>
					<div className="p-4 bg-grey-50 rounded-lg">
						<div className="flex flex-col gap-1 mb-4">
							<div className="text-sm text-grey-500">{t('review.campaignDuration')}</div>
							<DateRangeField
								hideLabel={true}
								value={[review.startDate, review.endDate]}
								onChange={handleDateRangeChange}
								minDate={today}
								maxDurationDays={maxCampaignDurationDays}
								readOnly={hasExistingInvoice}
								readOnlyReason={t('review.datesLockedByInvoice')}
								errors={dateErrors}
							/>
						</div>
						<div className="grid grid-cols-2 gap-3">
							<div className="flex flex-col gap-1">
								<div className="text-sm text-grey-500">{t('review.budgetLabel')}</div>
								<AmountField
									hideLabel={true}
									label={t('review.budgetLabel')}
									value={review.budget}
									onChange={(budget) => setReview({ ...review, budget })}
									onFocus={handleBudgetFocus}
									onBlur={handleBudgetBlur}
									errors={budgetErrors}
									readOnly={hasExistingInvoice}
									hint={budgetHint}
									locale={i18n.language}
									prefix="€"
									suffix="+ VAT"
								/>
							</div>

							{!hasExistingInvoice && (
								<BudgetPrediction
									prediction={budgetPrediction.data}
									isLoading={budgetPrediction.isPending}
									locations={review.adCampaignLocations}
									budgetEuros={review.budget}
									onBudgetSelect={(budgetEuros) => {
										const previousBudget = review.budget
										setReview({ ...review, budget: budgetEuros })
										trackBudgetSet(previousBudget, budgetEuros, 'predicted')
									}}
								/>
							)}
						</div>

						{!hasExistingInvoice && overlapsPeakAdSeason(review.startDate, review.endDate) && (
							<div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5 text-sm text-amber-800">
								<p className="font-medium">{t('review.peakSeasonTitle')}</p>
								<p className="mt-1">
									{t('review.peakSeasonBody')} <strong>{t('review.peakSeasonRecommendation')}</strong>
								</p>
							</div>
						)}

						<RequireAccess permission="addons:list">
							{campaignAddons.isLoading
								? (
									<div className="p-3 bg-blue-50 rounded-lg border border-blue-100">
										<div className="text-sm text-grey-500">{t('review.loadingAddons')}</div>
									</div>
								)
								: (
									<>
										{campaignAddons.data?.addons.map((addon: CampaignAddonData) => (
											<CampaignAddon
												key={addon.id}
												campaignId={campaignId}
												addon={addon}
												readOnly={hasExistingInvoice}
											/>
										))}
									</>
								)}
						</RequireAccess>

						{!hasExistingInvoice && (
							<div className="mt-3 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
								<small className="text-grey-700">
									{t('review.budgetCannotChange')}
								</small>
							</div>
						)}

						{invoiceCustomFields.isLoading
							? (
								<div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-100">
									<div className="text-sm text-grey-500">{t('common:common.loading')}</div>
								</div>
							)
							: (
								<div className="flex flex-col gap-4 mt-4">
									{renderableCustomFields.map((field: CustomFieldData) => {
										const value = customFields[field.id] ?? ''
										const isEmpty = !value || value.trim() === ''
										const isTooLong = value.length > 140
										const errors = [
											...(field.isRequired && isEmpty ? ['This custom field is required'] : []),
											...(isTooLong ? ['Value must be 140 characters or fewer'] : []),
										]
										return (
											<TextField
												key={field.id}
												label={field.name}
												value={value}
												onChange={(value) => setCustomFieldValue(field.id, value)}
												hint={field.isRequired
													? t('common:settings.customFields.required')
													: t('common:settings.customFields.optional')}
												errors={errors}
												maxLength={140}
												disabled={hasExistingInvoice}
											/>
										)
									})}
								</div>
							)}
					</div>
				</>
			)}

			{hasMetaChannel && !externalInvoicing && !isDemo && invoicingEnabled && !isBillingComplete && (
				<div className="flex flex-col gap-4">
					<p className="text-sm text-grey-700">{t('review.billingDetailsRequired')}</p>
					<BillingSummary />
					{can('profile:edit')
						? (
							<Button
								type="neutral"
								onClick={() => setIsEditingBillingDetails(true)}
								disabled={profiles.isLoading}
							>
								{t('common:settings.billing.editDetails')}
							</Button>
						)
						: null}
					<FormDialog
						open={isEditingBillingDetails}
						onClose={() => setIsEditingBillingDetails(false)}
						title={t('common:settings.billing.editDetails')}
						maxWidth="md"
					>
						{isEditingBillingDetails
							? (
								<BillingDetailsForm
									organisationId={organisationId}
									profile={primaryProfile}
									submitLabel={t('common:profiles.save')}
									onSaved={() => {
										setIsEditingBillingDetails(false)
										void billingValidation.refetch()
									}}
								/>
							)
							: null}
					</FormDialog>
				</div>
			)}

			<div className="flex flex-col items-center gap-2">
				<Button
					type="green"
					postfix="arrow"
					disabled={(hasMetaChannel && (budgetErrors.length > 0 || isStartDateInPast))
						|| isCreditedInvoiceBlocking || hasUnverifiedTaxID || isMissingRequiredCustomField
						|| hasCustomFieldValueTooLong || campaignQuota.isExhausted
						|| isPersistingChanges || isMissingImage}
					onClick={handleComplete}
				>
					{t('review.complete')}
				</Button>
				{isFullyServiced && (
					<div className="w-full max-w-xl rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
						{t('review.disabledServiceTier')}
					</div>
				)}
				{hasCreditedInvoice && (
					<div className="w-full max-w-xl rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
						{isJobillan
							? t('review.creditedInvoiceWarning')
							: t('review.disabledCreditedInvoice')}
					</div>
				)}
				{campaignQuota.isExhausted && (
					<div className="w-full max-w-xl rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
						{formatPeriodEnd(campaignQuota.periodEnd)
							? t('review.disabledCampaignLimitReachedWithDate', {
								date: formatPeriodEnd(campaignQuota.periodEnd),
							})
							: t('review.disabledCampaignLimitReached')}
						{' · '}
						<button
							type="button"
							onClick={requestMore.request}
							disabled={requestMore.isDisabled}
							className="cursor-pointer underline underline-offset-2 hover:no-underline disabled:cursor-default disabled:no-underline disabled:opacity-50"
						>
							{requestMore.isError
								? t('common:campaignQuota.requestFailed')
								: requestMore.hasSent
								? t('common:campaignQuota.requestSent')
								: t('common:campaignQuota.requestMore')}
						</button>
					</div>
				)}
				{hasMetaChannel && isStartDateInPast && (
					<small className="text-amber-700">{t('review.startDateInPast')}</small>
				)}
				{hasMetaChannel && hasUnverifiedTaxID && (
					<small className="text-amber-700">{t('review.disabledUnverifiedTaxId')}</small>
				)}
				{hasMetaChannel && !externalInvoicing && !isDemo && invoicingEnabled && !isBillingComplete && (
					<small className="text-amber-700">{t('review.disabledBillingIncomplete')}</small>
				)}
				{(isMissingRequiredCustomField || hasCustomFieldValueTooLong) && (
					<small className="text-amber-700">{t('review.disabledRequiredFields')}</small>
				)}
				{isPersistingChanges && <small className="text-amber-700">{t('review.disabledSaving')}</small>}
				{isMissingImage && <small className="text-amber-700">{t('review.disabledMissingImage')}</small>}
			</div>

			<CompletePopup
				ref={completePopupRef}
				campaignId={campaignId}
				channels={review.channels}
				onRedirectAfterComplete={onRedirectAfterComplete}
				onConfirm={handleConfirm}
				campaignTitle={campaignTitle ?? ''}
				startDate={review.startDate}
				endDate={review.endDate}
				locations={review.adCampaignLocations}
				customFields={customFields}
				customFieldsMetadata={renderableCustomFields}
				renderPreviews={renderPreviews}
			/>
		</div>
	)
}
